import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import patientRecordsService from '../../services/patientRecordsService';
import { FaSave, FaArrowLeft } from 'react-icons/fa';


const CompleteRegistrationForm = () => {
    const { patientId } = useParams();
    const navigate = useNavigate();

    const [patient, setPatient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        mrn_temporary: '',
        custom_mrn_suffix: '',
        insurance_provider: '',
        insurance_policy_number: '',
        insurance_subscriber_name: ''
    });

    const [mrnValidation, setMrnValidation] = useState({
        isChecking: false,
        message: '',
        isValid: false,
        isAvailable: false
    });

    const loadPatient = useCallback(async () => {
        try {
            setLoading(true);
            const response = await patientRecordsService.getPatientDetails(patientId);
            if (response.success) {
                const p = response.patient;
                setPatient(p);
                
                // Comprehensive field mapping from patient data
                const demographics = p.demographics || {};
                const medicalRecord = p.medical_record || {};
                const careStatus = p.care_status || {};
                const insuranceInfo = medicalRecord.insurance_info || [];
                const primaryInsurance = insuranceInfo[0] || {};
                
                // Debugging: Log patient data structure and key paths
                console.log('Patient data for auto-population:', p);
                console.log('Demographics object:', demographics);
                console.log('Medical record object:', medicalRecord);
                console.log('Insurance info array:', insuranceInfo);
                console.log('Primary insurance object:', primaryInsurance);
                
                // Log specific insurance field paths for debugging
                console.log('Insurance mapping debug:', {
                    'demographics.insurance_provider': demographics.insurance_provider,
                    'demographics.insurance_policy_number': demographics.insurance_policy_number, 
                    'demographics.insurance_subscriber_name': demographics.insurance_subscriber_name,
                    'primaryInsurance.provider': primaryInsurance.provider,
                    'primaryInsurance.policy_number': primaryInsurance.policy_number,
                    'medicalRecord.insurance_provider': medicalRecord.insurance_provider,
                    'p.insurance_provider': p.insurance_provider
                });
                
                // More robust auto-population with multiple fallbacks
                const newFormData = {
                    ...formData,
                    // Basic MRN info
                    mrn_temporary: p.mrn_temporary || p.mrn || p.current_mrn || '',
                    
                    // Insurance information - now correctly mapping from demographics first
                    insurance_provider: demographics.insurance_provider || primaryInsurance.provider || medicalRecord.insurance_provider || p.insurance_provider || '',
                    insurance_policy_number: demographics.insurance_policy_number || primaryInsurance.policy_number || medicalRecord.insurance_policy_number || p.insurance_policy_number || '',
                    insurance_subscriber_name: demographics.insurance_subscriber_name || primaryInsurance.subscriber_name || demographics.full_name || `${demographics.first_name || ''} ${demographics.last_name || ''}`.trim() || p.full_name || ''
                };
                
                console.log('Auto-populated form data:', newFormData);
                console.log('Final insurance mapping results:', {
                    'insurance_provider': newFormData.insurance_provider,
                    'insurance_policy_number': newFormData.insurance_policy_number,
                    'insurance_subscriber_name': newFormData.insurance_subscriber_name
                });
                setFormData(newFormData);
            } else {
                setError(response.error);
            }
        } catch (e) {
            setError('Failed to load patient');
        } finally {
            setLoading(false);
        }
    }, [patientId]);

    useEffect(() => {
        if (patientId) loadPatient();
    }, [patientId, loadPatient]);

    const validateCustomMRN = useCallback(async (suffix) => {
        if (!suffix || suffix.length < 2) {
            setMrnValidation({
                isChecking: false,
                message: suffix ? 'MRN must be at least 2 characters' : '',
                isValid: false,
                isAvailable: false
            });
            return;
        }

        if (suffix.length > 15) {
            setMrnValidation({
                isChecking: false,
                message: 'MRN cannot exceed 15 characters',
                isValid: false,
                isAvailable: false
            });
            return;
        }

        // Support multiple MRN formats for backward compatibility
        const newFormatRegex = /^[A-Za-z]{1,5}[0-9]+$/;  // 1-5 letters + numbers
        const legacyFormatRegex = /^[A-Z]{3}[0-9]{6}$/;  // Legacy 3 letters + 6 numbers
        const oldFormatRegex = /^MRN-[A-Za-z0-9]+$/;     // Old MRN- prefix format
        
        if (!newFormatRegex.test(suffix) && !legacyFormatRegex.test(suffix) && !oldFormatRegex.test(suffix)) {
            setMrnValidation({
                isChecking: false,
                message: 'MRN must start with 1-5 alphabetic characters followed by numeric characters',
                isValid: false,
                isAvailable: false
            });
            return;
        }

        try {
            setMrnValidation(prev => ({ ...prev, isChecking: true }));
            
            const response = await patientRecordsService.validateCustomMRN({
                mrn_suffix: suffix.toUpperCase()
            });

            if (response.success) {
                setMrnValidation({
                    isChecking: false,
                    message: response.data.message,
                    isValid: response.data.is_valid_format,
                    isAvailable: response.data.is_available
                });
            } else {
                setMrnValidation({
                    isChecking: false,
                    message: response.error || 'Failed to validate MRN',
                    isValid: false,
                    isAvailable: false
                });
            }
        } catch (error) {
            setMrnValidation({
                isChecking: false,
                message: 'Error validating MRN',
                isValid: false,
                isAvailable: false
            });
        }
    }, []);

    // Debounced MRN validation
    useEffect(() => {
        const timer = setTimeout(() => {
            if (formData.custom_mrn_suffix) {
                validateCustomMRN(formData.custom_mrn_suffix);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [formData.custom_mrn_suffix, validateCustomMRN]);


    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate custom MRN if provided
        if (formData.custom_mrn_suffix) {
            if (!mrnValidation.isValid || !mrnValidation.isAvailable) {
                toast.error('Please fix the MRN issues before submitting');
                return;
            }
        }
        
        setSaving(true);
        
        try {
            // Create payload with proper data types and patient ID
            const payload = {
                patient_id: patientId,
                ...formData,
                custom_mrn_suffix: formData.custom_mrn_suffix || null,
                // Ensure assigned_services is always an array
                assigned_services: Array.isArray(formData.assigned_services) ? formData.assigned_services : []
            };
            
            console.log('Submitting registration payload:', payload);
            
            const response = await patientRecordsService.completePatientRegistration(payload);
            
            if (response.success) {
                toast.success(response.message || 'Registration completed successfully');
                // Force page refresh to ensure PatientDetailView loads fresh data
                window.location.href = `/patient-details/${patientId}`;
            } else {
                console.error('Registration failed:', response);
                toast.error(response.error || 'Failed to complete registration');
            }
        } catch (err) {
            console.error('Registration submission error:', err);
            toast.error('An error occurred while completing registration');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <Container className="p-5 text-center">
                <Spinner animation="border" />
                <p className="mt-2">Loading patient...</p>
            </Container>
        );
    }

    if (error) {
        return (
            <Container className="p-5">
                <Alert variant="danger">
                    <Alert.Heading>Error</Alert.Heading>
                    <p>{error}</p>
                    <Button variant="outline-danger" onClick={loadPatient}>Retry</Button>
                </Alert>
            </Container>
        );
    }

    return (
        <Container fluid className="p-4">
            <Row className="mb-4 align-items-center">
                <Col>
                    <h3>Complete Registration: {patient.demographics?.full_name}</h3>
                    <p className="text-muted">Temporary MRN: {patient.mrn_temporary}</p>
                </Col>
                <Col className="text-end">
                    <Button variant="outline-secondary" onClick={() => navigate(`/patient-details/${patientId}`)}>
                        <FaArrowLeft className="me-1" /> Back to Details
                    </Button>
                </Col>
            </Row>

            <Card className="shadow-sm border-0">
                <Card.Body>
                    <Form onSubmit={handleSubmit}>
                        {/* Custom MRN Section */}
                        <div className="mb-4 p-3 bg-light rounded">
                            <h5 className="mb-3">Medical Record Number (MRN)</h5>
                            <Row>
                                <Col md={6}>
                                    <Form.Group>
                                        <Form.Label>
                                            Custom MRN Suffix (Optional)
                                            <small className="text-muted ms-2">Leave empty for auto-generation</small>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={formData.custom_mrn_suffix}
                                            onChange={(e) => {
                                                const value = e.target.value.toUpperCase();
                                                // Support new flexible format: 1-5 letters followed by digits
                                                let filteredValue = '';
                                                let letterCount = 0;
                                                let hasStartedNumbers = false;
                                                
                                                for (let i = 0; i < value.length && i < 15; i++) {
                                                    const char = value[i];
                                                    
                                                    if (/[A-Z]/.test(char) && !hasStartedNumbers && letterCount < 5) {
                                                        // Letters allowed at the beginning (up to 5)
                                                        filteredValue += char;
                                                        letterCount++;
                                                    } else if (/[0-9]/.test(char) && letterCount > 0) {
                                                        // Numbers allowed after at least one letter
                                                        filteredValue += char;
                                                        hasStartedNumbers = true;
                                                    }
                                                }
                                                setFormData({...formData, custom_mrn_suffix: filteredValue});
                                            }}
                                            placeholder="ABC123456 or PATIENT12345"
                                            maxLength={15}
                                            style={{ fontFamily: 'monospace' }}
                                        />
                                        {formData.custom_mrn_suffix && (
                                            <div className="mt-2">
                                                <small className="d-block">
                                                    <strong>Preview:</strong> {formData.custom_mrn_suffix}
                                                    {mrnValidation.isChecking && (
                                                        <Spinner size="sm" className="ms-2" />
                                                    )}
                                                </small>
                                                {mrnValidation.message && (
                                                    <small className={`d-block mt-1 ${
                                                        mrnValidation.isValid && mrnValidation.isAvailable 
                                                            ? 'text-success' 
                                                            : 'text-danger'
                                                    }`}>
                                                        {mrnValidation.isValid && mrnValidation.isAvailable ? '✓' : '✗'} {mrnValidation.message}
                                                    </small>
                                                )}
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6} className="d-flex align-items-center">
                                    <div className="text-muted">
                                        <small>
                                            <strong>Format:</strong> 1-5 alphabetic characters followed by numeric characters<br/>
                                            <strong>Examples:</strong> A123456, ABC123456, PATIENT12345, XYZ789012<br/>
                                            <strong>Auto-generated:</strong> If left empty, system will generate automatically
                                        </small>
                                    </div>
                                </Col>
                            </Row>
                        </div>


                        <h5 className="mt-4">Insurance Information</h5>
                        <Row className="mb-3">
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Provider</Form.Label>
                                    <Form.Control 
                                        value={formData.insurance_provider} 
                                        onChange={(e) => setFormData({...formData, insurance_provider: e.target.value})}
                                        placeholder="Enter insurance provider"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Policy Number</Form.Label>
                                    <Form.Control 
                                        value={formData.insurance_policy_number} 
                                        onChange={(e) => setFormData({...formData, insurance_policy_number: e.target.value})}
                                        placeholder="Enter policy number"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Subscriber Name</Form.Label>
                                    <Form.Control 
                                        value={formData.insurance_subscriber_name} 
                                        onChange={(e) => setFormData({...formData, insurance_subscriber_name: e.target.value})}
                                        placeholder="Enter subscriber name"
                                    />
                                    {formData.insurance_subscriber_name && patient?.demographics?.full_name && (
                                        <Form.Text className="text-success">
                                            ✓ Auto-populated from patient demographics
                                        </Form.Text>
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>




                        <div className="text-end">
                            <Button type="submit" variant="success" disabled={saving}>
                                {saving ? (<><Spinner size="sm" className="me-2" />Saving...</>) : (<><FaSave className="me-2" />Complete Registration</>)}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default CompleteRegistrationForm; 